import { API_BASE } from "../config";
import { formatDateOnly } from '../utils/dates';
import React, { useState, useEffect } from 'react';
import { printFileContent, printHTMLDocument } from '../utils/printHelper';
import { authedBlobUrl, revokeBlobUrl } from '../utils/authedBlobUrl';
import XaiPromptPanel from './XaiPromptPanel';
import { CitationsButton } from './Citations';
import { citationCount } from '../utils/xaiHelpers';
import UhcCompatBadge from './UhcCompatBadge';
import CostChip from './CostChip';
import { useCreditSpend } from '../contexts/CreditSpendContext';
import { handleAIError } from '../utils/aiErrorHandler';

const MealPlanManagement = ({ fetchWithAuth, fetchWithAuthFormData, onOpenAIAgent, refreshKey, onDataChanged }) => {
  const { spend } = useCreditSpend();  // B-CREDIT1 fix 2: meal_plan_import = 3 credits (chip + low-balance confirm)
  const [mealPlans, setMealPlans] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showDetailedView, setShowDetailedView] = useState(false);
  const [selectedPlanDetails, setSelectedPlanDetails] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [showFileViewer, setShowFileViewer] = useState(false);
  const [fileContent, setFileContent] = useState('');
  const [showRecipeViewer, setShowRecipeViewer] = useState(false);
  const [showDayRecipeViewer, setShowDayRecipeViewer] = useState(false);
  const [selectedDayRecipes, setSelectedDayRecipes] = useState(null);
  // H7: the two recipe iframes render from blob: URLs fetched with the Authorization header,
  // instead of an API URL carrying the session token as a query param.
  const [recipeViewerUrl, setRecipeViewerUrl] = useState(null);
  const [dayRecipeUrl, setDayRecipeUrl] = useState(null);

  // Two-phase generation: the recipe card is built on the tap, not with the plan. Measured
  // 7.4s cold / 0.13s warm through Cloudflare, so the first open needs a real progress state —
  // 7 seconds of an unexplained dead button is how a working feature reads as broken.
  const [cardBuilding, setCardBuilding] = useState(null);   // schedule_id being built
  const [cardError, setCardError] = useState(null);
  // WHICH meal the error belongs to. Without this the message renders under every meal that
  // has no card yet, so one failure looks like nine.
  const [cardErrorFor, setCardErrorFor] = useState(null);
  const [cardSafety, setCardSafety] = useState(null);       // #93 — the verdict, finally shown
  // Set to the ORIGINAL meal name when the server repaired this slot with a different dish.
  const [cardReplaced, setCardReplaced] = useState(null);
  // schedule_ids whose card we built THIS session. Held in state rather than mutated onto the
  // meal object: `meal` comes from selectedPlanDetails, and writing to it would be a silent
  // prop mutation React never re-renders from — it would work today only because nothing
  // renders off it, and break the moment someone made the label depend on it.
  const [builtCards, setBuiltCards] = useState(() => new Set());

  // Import modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importMode, setImportMode] = useState('file');
  const [importText, setImportText] = useState('');
  const [importUrl, setImportUrl] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importPlanName, setImportPlanName] = useState('');
  const [importPlanType, setImportPlanType] = useState('maintenance');
  const [importFile, setImportFile] = useState(null);

  // XAI state
  const [xaiPlanId, setXaiPlanId] = useState(null);
  const [xaiPromptData, setXaiPromptData] = useState(null);
  const [lastGeneratedPromptData, setLastGeneratedPromptData] = useState(null);

  // Renew state (Phase D)
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [renewingPlan, setRenewingPlan] = useState(null);
  const [renewStartDate, setRenewStartDate] = useState('');
  const [renewRecurring, setRenewRecurring] = useState(false);
  const [renewDurationDays, setRenewDurationDays] = useState(7);
  const [renewLoading, setRenewLoading] = useState(false);

  const [formData, setFormData] = useState({
    plan_name: '',
    plan_type: 'maintenance',
    target_calories_daily: 2200,
    bmr_baseline: null,
    activity_multiplier: 1.5,
    calorie_adjustment_goal: 0,
    
    target_protein_percentage: 30,
    target_carbs_percentage: 40,
    target_fat_percentage: 30,
    
    meals_per_day: 3,
    snacks_per_day: 2,
    
    target_water_ml: 2500,
    diet_category: 'standard_balanced',
    cuisine_preferences: ['no_preference'],
    meal_timing_strategy: 'flexible',
    eating_window_start: '08:00',
    eating_window_end: '20:00',
    meal_prep_batch_size: 7,
    
    monitor_weight: true,
    monitor_body_composition: true,
    monitor_energy_levels: true,
    monitor_digestion: false,
    recalibration_frequency_days: 14,
    
    is_active: true,
    start_date: new Date().toISOString().split('T')[0],
    end_date: null
  });

  useEffect(() => {
    loadMealPlans();
  }, [refreshKey]);

  const loadMealPlans = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/meal-plans/list?include_inactive=true`);
      if (response.ok) {
        const data = await response.json();
        setMealPlans(data.plans || []);
      }
    } catch (error) {
      console.error('Failed to load meal plans:', error);
    }
  };

  const loadPlanDetails = async (planId) => {
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/meal-plans/${planId}/details`);
      if (response.ok) {
        const details = await response.json();
        setSelectedPlanDetails(details);
        setShowDetailedView(true);
      }
    } catch (error) {
      console.error('Failed to load plan details:', error);
      alert('Unable to load detailed meal plan. Please try again.');
    }
  };

  // Close the file viewer AND release the blob URL. A blob: URL pins its bytes for the lifetime of
  // the document unless revoked, so every viewer-close path must go through here — otherwise each
  // file opened leaks until the tab is closed. (H7, 2026-08-03. Mirrors RecordsPage's
  // closeMedicalFileViewer.)
  const closeFileViewer = () => {
    setFileContent(prev => {
      if (typeof prev === 'string' && prev.startsWith('__PDF__')) {
        revokeBlobUrl(prev.replace('__PDF__', ''));
      }
      return '';
    });
    setShowFileViewer(false);
  };

  // ── H7 recipe viewers: fetch authenticated, render from a blob: URL, revoke on close ──────
  // The endpoints return SELF-CONTAINED HTML (verified 2026-08-03: no root-relative /api src or
  // href), which is what makes a blob: URL safe here — a blob document has an opaque origin, so a
  // root-relative URL added to that HTML later would silently fail to load.
  const openRecipeViewer = async (planId) => {
    setShowRecipeViewer(true);
    setRecipeViewerUrl(null);
    try {
      setRecipeViewerUrl(await authedBlobUrl(
        fetchWithAuth, `${API_BASE}/api/meal-plans/${planId}/recipes/view`));
    } catch (err) {
      console.error('Failed to load recipe view:', err);
    }
  };

  const closeRecipeViewer = () => {
    revokeBlobUrl(recipeViewerUrl);
    setRecipeViewerUrl(null);
    setShowRecipeViewer(false);
  };

  const openDayRecipeViewer = async (planId, dayNumber) => {
    setDayRecipeUrl(null);
    try {
      setDayRecipeUrl(await authedBlobUrl(
        fetchWithAuth, `${API_BASE}/api/meal-plans/${planId}/recipe?day=${dayNumber}`));
    } catch (err) {
      console.error('Failed to load day recipe:', err);
    }
  };

  // ── THE TAP: skeleton meal -> full recipe card ────────────────────────────────────────────
  // Two-phase generation ships a PLAN, not 70 recipe cards: names, ingredients, macros and a
  // completed safety screen, in ~11s for 3 days instead of 270s. The quantities, instructions,
  // prep/cook times and the photo are built WHEN A MEAL IS OPENED.
  //
  // The backend half of that contract has been live and working since Phase A — `/details`
  // ships `schedule_id` + `has_card` on every meal for exactly this purpose, and the endpoint
  // measures 7.4s cold / 0.13s warm through Cloudflare. Nothing on this side called it, so a
  // generated plan showed "No instructions listed" and the recipe was unreachable. This is that
  // missing half.
  //
  // `store_card` writes the finished card back into `meals_json`, so once this returns the
  // EXISTING day viewer renders the real recipe — no change needed there.
  const ensureCardThenViewRecipe = async (planId, meal, dayNumber) => {
    const key = meal.schedule_id;

    // Already built — either an older eagerly-filled plan, or we built it earlier this session.
    // Straight through, no spinner: the server returns the stored card in 0.13s.
    if (meal.has_card || builtCards.has(key) || !key) {
      setShowDayRecipeViewer(true);
      await openDayRecipeViewer(planId, dayNumber);
      return;
    }

    setCardBuilding(key);
    setCardError(null);
    setCardErrorFor(null);
    try {
      const res = await fetchWithAuth(
        `${API_BASE}/api/meal-plans/${planId}/schedule/${key}/card`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });

      if (res.status === 409) {
        // The server repairs a rejected card itself — it regenerates the same approved
        // ingredients at a more moderate portion, then falls back to a catalog recipe that
        // already passed this user's complete UHC. Reaching 409 means EVERY layer failed, which
        // is rare and is logged at ERROR server-side.
        //
        // It must never tell the user to regenerate their plan over one recipe.
        setCardError('We could not put together a version of this meal that fits your health '
                     + 'profile. The rest of your plan is unaffected — try another meal, and '
                     + 'we will keep working on this one.');
        setCardErrorFor(key);
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      // #93 — the verdict the backend has always computed and nobody ever showed.
      setCardSafety(data.safety_validation || null);
      // The server swapped this slot for a safer dish. Say so — silently serving something
      // other than the meal the user tapped is worse than the swap itself.
      setCardReplaced(data.replaced ? (data.replaced_from || null) : null);
      // Mark it built so a second tap is a read, not another generation.
      setBuiltCards((prev) => new Set(prev).add(key));

      setShowDayRecipeViewer(true);
      await openDayRecipeViewer(planId, dayNumber);
    } catch (err) {
      console.error('Failed to build recipe card:', err);
      setCardError('We could not build this recipe just now. Please try again.');
      setCardErrorFor(key);
    } finally {
      setCardBuilding(null);
    }
  };

  const closeDayRecipeViewer = () => {
    revokeBlobUrl(dayRecipeUrl);
    setDayRecipeUrl(null);
    setShowDayRecipeViewer(false);
    setSelectedDayRecipes(null);
    // Clear the verdict with the modal it belongs to. A stale `caution` left in state would
    // render over the NEXT recipe opened — attributing one meal's interaction to another meal
    // is worse than showing nothing.
    setCardSafety(null);
    setCardError(null);
    setCardErrorFor(null);
    setCardReplaced(null);
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    
    setFormData({
      plan_name: plan.plan_name || '',
      plan_type: plan.plan_type || 'maintenance',
      target_calories_daily: plan.target_calories_daily || 2200,
      bmr_baseline: plan.bmr_baseline,
      activity_multiplier: plan.activity_multiplier || 1.5,
      calorie_adjustment_goal: plan.calorie_adjustment_goal || 0,
      
      target_protein_percentage: plan.target_protein_percentage || 30,
      target_carbs_percentage: plan.target_carbs_percentage || 40,
      target_fat_percentage: plan.target_fat_percentage || 30,
      
      meals_per_day: plan.meals_per_day || 3,
      snacks_per_day: plan.snacks_per_day || 2,
      
      target_water_ml: plan.target_water_ml || 2500,
      diet_category: plan.diet_category || 'standard_balanced',
      cuisine_preferences: plan.cuisine_preferences || ['no_preference'],
      meal_timing_strategy: plan.meal_timing_strategy || 'flexible',
      eating_window_start: plan.eating_window_start || '08:00',
      eating_window_end: plan.eating_window_end || '20:00',
      meal_prep_batch_size: plan.meal_prep_batch_size || 7,
      
      monitor_weight: plan.monitor_weight !== false,
      monitor_body_composition: plan.monitor_body_composition !== false,
      monitor_energy_levels: plan.monitor_energy_levels !== false,
      monitor_digestion: plan.monitor_digestion || false,
      recalibration_frequency_days: plan.recalibration_frequency_days || 14,
      
      is_active: plan.is_active,
      start_date: plan.start_date || new Date().toISOString().split('T')[0],
      end_date: plan.end_date
    });
    
    setShowForm(true);
  };

  const handleDelete = async (planId) => {
    if (!confirm('Are you sure you want to delete this meal plan?')) return;
    
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/meal-plans/${planId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        // Clear local state
        setMealPlans(prev => prev.filter(plan => plan.id !== planId));
        setSelectedPlanDetails(null);
        setShowDetailedView(false);
        setEditingPlan(null);
        setShowRecipeViewer(false);
        setShowDayRecipeViewer(false);
        setSelectedDayRecipes(null);
        // Trigger parent refresh (updates Recipe Management, Shopping Lists, Pantries)
        if (onDataChanged) {
          onDataChanged();
        }
      } else {
        alert('Failed to delete meal plan');
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete meal plan');
    }
  };

  // ── Radio button active toggle (Phase D) ──
  const handleSetActivePlan = async (planId) => {
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/meal-plans/${planId}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: true })
      });
      if (response.ok) {
        loadMealPlans();
        if (onDataChanged) onDataChanged();
      }
    } catch (error) {
      console.error('Activate plan error:', error);
    }
  };

  // ── Renew handler (Phase D) ──
  const openRenewModal = (plan) => {
    setRenewingPlan(plan);
    setRenewStartDate(new Date().toISOString().split('T')[0]);
    setRenewRecurring(plan.recurring_enabled || false);
    setRenewDurationDays(plan.recurring_duration_days || 7);
    setShowRenewModal(true);
  };

  const handleRenew = async () => {
    if (!renewingPlan || !renewStartDate) return;
    setRenewLoading(true);
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/meal-plans/${renewingPlan.id}/renew`, {
        method: 'POST',
        body: JSON.stringify({
          start_date: renewStartDate,
          recurring_enabled: renewRecurring,
          recurring_duration_days: renewRecurring ? renewDurationDays : null,
        })
      });
      if (response.ok) {
        setShowRenewModal(false);
        setRenewingPlan(null);
        loadMealPlans();
        if (onDataChanged) onDataChanged();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to renew plan');
      }
    } catch (error) {
      console.error('Renew error:', error);
      alert('Failed to renew meal plan');
    } finally {
      setRenewLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.plan_name.trim()) {
      alert('Please enter a plan name');
      return;
    }

    const macroTotal = formData.target_protein_percentage + formData.target_carbs_percentage + formData.target_fat_percentage;
    if (macroTotal !== 100) {
      alert(`Macro percentages must total 100% (currently ${macroTotal}%)`);
      return;
    }

    try {
      // STEP 1: Create/update meal plan WITHOUT file attachment
      const payload = {
        ...formData
        // DO NOT include attached_file in payload
      };

      const url = editingPlan 
        ? `${API_BASE}/api/meal-plans/${editingPlan.id}`
        : `${API_BASE}/api/meal-plans/create`;
      
      const method = editingPlan ? 'PUT' : 'POST';
      
      const response = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save meal plan');
      }

      const savedPlan = await response.json();

      // STEP 2: If there's a file, upload it separately
      if (uploadedFile) {
        const uploadResponse = await fetchWithAuth(
          `${API_BASE}/api/meal-plans/${savedPlan.plan_id}/upload-file`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(uploadedFile)
          }
        );

        if (!uploadResponse.ok) {
          const errorData = await uploadResponse.json();
          throw new Error(`Failed to upload file: ${JSON.stringify(errorData)}`);
        }
      }

      // STEP 3: Reload plans and close form
      loadMealPlans();
      closeForm();
      
    } catch (error) {
      console.error('Submit error:', error);
      alert(error.message || 'Failed to save meal plan');
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingPlan(null);
    setShowAdvanced(false);
    setUploadedFile(null);
    setDragActive(false);
    // Reset form
    setFormData({
      plan_name: '',
      plan_type: 'maintenance',
      target_calories_daily: 2200,
      bmr_baseline: null,
      activity_multiplier: 1.5,
      calorie_adjustment_goal: 0,
      target_protein_percentage: 30,
      target_carbs_percentage: 40,
      target_fat_percentage: 30,
      meals_per_day: 3,
      snacks_per_day: 2,
      target_water_ml: 2500,
      diet_category: 'standard_balanced',
      cuisine_preferences: ['no_preference'],
      meal_timing_strategy: 'flexible',
      eating_window_start: '08:00',
      eating_window_end: '20:00',
      meal_prep_batch_size: 7,
      monitor_weight: true,
      monitor_body_composition: true,
      monitor_energy_levels: true,
      monitor_digestion: false,
      recalibration_frequency_days: 14,
      is_active: true,
      start_date: new Date().toISOString().split('T')[0],
      end_date: null
    });
  };

  const toggleCuisine = (cuisine) => {
    setFormData(prev => ({
      ...prev,
      cuisine_preferences: prev.cuisine_preferences.includes(cuisine)
        ? prev.cuisine_preferences.filter(c => c !== cuisine)
        : [...prev.cuisine_preferences, cuisine]
    }));
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (file) => {
    // Validate file type - accept PDF, Word, TXT, Images (view-only documents)
    const validTypes = [
      'application/pdf',
      'text/plain',
      'image/jpeg',
      'image/jpg',
      'image/png'
    ];
    
    const validExtensions = /\.(pdf|txt|jpg|jpeg|png)$/i;
    
    if (!validTypes.includes(file.type) && !file.name.match(validExtensions)) {
      alert('Please upload a valid meal plan file (PDF, TXT, or JPG/PNG)');
      return;
    }

    // Read file content
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const content = e.target.result;
        setUploadedFile({
          name: file.name,
          type: file.type,
          size: file.size,
          content: content
        });
      } catch (error) {
        console.error('Error reading file:', error);
        alert('Failed to read file. Please try again.');
      }
    };
    
    // Use readAsDataURL for binary files (PDF, Word, Images)
    // This preserves file integrity for later display
    reader.readAsDataURL(file);
  };

  // === Import Modal Handlers ===

  const handleImportFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleImportFile(e.target.files[0]);
    }
  };

  const handleImportFile = (file) => {
    const validTypes = ['application/pdf', 'text/plain', 'image/jpeg', 'image/jpg', 'image/png'];
    const validExtensions = /\.(pdf|txt|jpg|jpeg|png)$/i;
    if (!validTypes.includes(file.type) && !file.name.match(validExtensions)) {
      alert('Please upload a valid file (PDF, TXT, or JPG/PNG)');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setImportFile({
        name: file.name,
        type: file.type,
        size: file.size,
        content: e.target.result
      });
    };
    reader.readAsDataURL(file);
  };

  const handleImportDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleImportDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleImportFile(e.dataTransfer.files[0]);
    }
  };

  const handleImportParse = async () => {
    setImportLoading(true);
    try {
      const payload = {};
      if (importMode === 'file' && importFile) {
        payload.file = importFile;
      } else if (importMode === 'text' && importText.trim()) {
        payload.text = importText.trim();
      } else if (importMode === 'url' && importUrl.trim()) {
        payload.url = importUrl.trim();
      } else {
        alert('Please provide a file, text, or URL to parse.');
        setImportLoading(false);
        return;
      }

      const response = await fetchWithAuth(`${API_BASE}/api/meal-plans/import-parse`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        // Out of credits / not on this plan → the unified AI limit modal (B-CREDIT1 fix 2).
        if (await handleAIError(response)) return;
        const err = await response.json().catch(() => ({}));
        // `detail` is a string, a 422 validation array, or the credit gate's object — always text.
        const detail = Array.isArray(err.detail) ? err.detail.map(d => d.msg || d).join(', ')
          : (typeof err.detail === 'string' ? err.detail : (err.detail?.message || 'Parse failed'));
        throw new Error(detail);
      }

      const data = await response.json();
      setImportPreview(data.preview);
      setImportPlanName(data.preview.plan_name || 'Imported Meal Plan');
    } catch (error) {
      alert(error.message || 'Failed to parse meal plan');
    } finally {
      setImportLoading(false);
    }
  };

  const handleImportConfirm = async () => {
    if (!importPreview || !importPlanName.trim()) {
      alert('Please provide a plan name.');
      return;
    }

    setImportLoading(true);
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/meal-plans/import-confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan_name: importPlanName.trim(),
          plan_type: importPlanType,
          days: importPreview.days
        })
      });

      if (!response.ok) {
        const err = await response.json();
        const detail = Array.isArray(err.detail) ? err.detail.map(d => d.msg || d).join(', ') : (err.detail || 'Import failed');
        throw new Error(detail);
      }

      const result = await response.json();
      alert(`Plan "${result.plan_name}" created with ${result.total_meals} meals across ${result.total_days} days.`);

      // Reset and close
      closeImportModal();
      loadMealPlans();
      if (onDataChanged) onDataChanged();

    } catch (error) {
      alert(error.message || 'Failed to create meal plan');
    } finally {
      setImportLoading(false);
    }
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportMode('file');
    setImportText('');
    setImportUrl('');
    setImportFile(null);
    setImportPreview(null);
    setImportPlanName('');
    setImportPlanType('maintenance');
    setImportLoading(false);
  };

  const updateImportMeal = (dayIdx, mealIdx, field, value) => {
    const updated = { ...importPreview };
    updated.days = updated.days.map((d, di) => {
      if (di !== dayIdx) return d;
      return {
        ...d,
        meals: d.meals.map((m, mi) => {
          if (mi !== mealIdx) return m;
          return { ...m, [field]: value };
        })
      };
    });
    setImportPreview(updated);
  };

  const removeImportMeal = (dayIdx, mealIdx) => {
    const updated = { ...importPreview };
    updated.days = updated.days.map((d, di) => {
      if (di !== dayIdx) return d;
      return { ...d, meals: d.meals.filter((_, mi) => mi !== mealIdx) };
    });
    // Remove empty days
    updated.days = updated.days.filter(d => d.meals.length > 0);
    setImportPreview(updated);
  };

  const cuisineOptions = [
    { value: 'italian', label: 'Italian' },
    { value: 'mexican', label: 'Mexican' },
    { value: 'asian', label: 'Asian' },
    { value: 'indian', label: 'Indian' },
    { value: 'mediterranean', label: 'Mediterranean' },
    { value: 'american', label: 'American' }
  ];

  const macroTotal = formData.target_protein_percentage + formData.target_carbs_percentage + formData.target_fat_percentage;
  const cals = formData.target_calories_daily;
  const macros = {
    protein: Math.round((cals * (formData.target_protein_percentage / 100)) / 4),
    carbs: Math.round((cals * (formData.target_carbs_percentage / 100)) / 4),
    fat: Math.round((cals * (formData.target_fat_percentage / 100)) / 9)
  };

  return (
    <div className="mpm-container">
      {/* Header */}
      <div className="mpm-header">
        <h3 className="mpm-title">
          Meal Plan Management
        </h3>
        <div className="mpm-headerActions">
          <button
            onClick={() => setShowImportModal(true)}
            className="mpm-newManualBtn"
          >
            Import Plan
          </button>
          <button 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (typeof onOpenAIAgent === 'function') {
                onOpenAIAgent('meal-plan');
              } else {
                console.error('onOpenAIAgent is not a function:', onOpenAIAgent);
                alert('AI Agent function not available');
              }
            }}
            type="button"
            className="mpm-newAiBtn"
            onMouseOver={(e) => e.target.style.transform = 'translateY(-1px)'}
            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
          >
            New Plan - AI Generated
          </button>
        </div>
      </div>

      {/* Meal Plans List */}
      {mealPlans.length === 0 ? (
        <div className="mpm-emptyState">
          <p className="mpm-emptyIcon"></p>
          <p className="mpm-emptyText">No meal plans created yet. Create your first meal plan!</p>
        </div>
      ) : (
        <div className="mpm-planList">
          {mealPlans.map(plan => (
            <div
              key={plan.id}
              className={`mpm-planCard ${plan.is_active ? 'mpm-planCard--active' : ''}`}
            >
              <div className="mpm-planCardRow">
                {/* Radio button for active selection */}
                <div
                  className={`mpm-radioBtn ${plan.is_active ? 'mpm-radioBtn--selected' : ''}`}
                  onClick={() => { if (!plan.is_active) handleSetActivePlan(plan.id); }}
                  title={plan.is_active ? 'Currently active' : 'Click to activate this plan'}
                >
                  {plan.is_active && <div className="mpm-radioDot" />}
                </div>
                <div className="mpm-planCardContent">
                  <h4 className="mpm-planName">
                    {plan.plan_name}
                    {plan.human_edited && <span className="mpm-editedBadge">Edited</span>}
                  </h4>
                  <div className="mpm-planMeta">
                    <span>
                      {plan.ai_generated
                        ? (plan.fitness_program_name
                            ? `AI Generated for '${plan.fitness_program_name}'`
                            : 'AI Generated')
                        : 'Manual Entry'}
                    </span>
                    {plan.start_date && (
                      <span>Start: {formatDateOnly(plan.start_date)}</span>
                    )}
                    {plan.end_date && (
                      <span>End: {formatDateOnly(plan.end_date)}</span>
                    )}
                    <span>{plan.target_calories_daily} cal/day</span>
                    <span className={plan.is_active ? 'mpm-statusActive' : 'mpm-statusInactive'}>
                      {plan.is_active ? 'ACTIVE' : 'Inactive'}
                    </span>
                    {plan.recurring_enabled && (
                      <span>Recurring: {plan.recurring_duration_days || 7}-day</span>
                    )}
                  </div>
                </div>
              </div>
              {/* Apple 1.4.1 (2026-10-17): the standard Citations control, in the plan's header.
                  Same toggle state and data as before; only its label and position changed. */}
              {plan.ai_generated && (
                <div className="cite-inline">
                  <CitationsButton
                    open={xaiPlanId === plan.id}
                    count={citationCount(plan.generation_context?.prompt_data || null, 'Meal Plan Generator')}
                    onToggle={() => {
                      if (xaiPlanId === plan.id) { setXaiPlanId(null); setXaiPromptData(null); }
                      else {
                        setXaiPlanId(plan.id);
                        setXaiPromptData(plan.generation_context?.prompt_data || lastGeneratedPromptData);
                      }
                    }}
                  />
                  {xaiPlanId === plan.id && xaiPromptData && (
                    <XaiPromptPanel promptData={xaiPromptData} featureName="Meal Plan Generator" />
                  )}
                </div>
              )}
                {plan.attached_file_path && (
                  <div className="mpm-attachmentRow">
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        setEditingPlan(plan);
                        const fileName = plan.attached_file_name || '';
                        const isPdf = fileName.toLowerCase().endsWith('.pdf');
                        if (isPdf) {
                          // H7: fetch with the Authorization header and render from a blob: URL —
                          // the token must never appear in a URL. The __PDF__ sentinel and the
                          // iframe below are unchanged; only the source of the URL differs.
                          setFileContent('Loading...');
                          setShowFileViewer(true);
                          try {
                            const blobUrl = await authedBlobUrl(
                              fetchWithAuth,
                              `${API_BASE}/api/meal-plans/${plan.id}/download-file`
                            );
                            setFileContent(`__PDF__${blobUrl}`);
                          } catch (err) {
                            console.error('Failed to load meal plan file:', err);
                            setFileContent('Failed to load file content');
                          }
                        } else {
                          setFileContent('Loading...');
                          setShowFileViewer(true);
                          try {
                            const response = await fetchWithAuth(
                              `${API_BASE}/api/meal-plans/${plan.id}/download-file`
                            );
                            if (response.ok) {
                              const text = await response.text();
                              setFileContent(text);
                            } else {
                              setFileContent('Failed to load file content');
                            }
                          } catch (err) {
                            setFileContent('Error loading file: ' + err.message);
                          }
                        }
                      }}
                      className="mpm-attachmentBtn"
                    >
                      {plan.attached_file_name}
                    </button>
                  </div>
                )}
              <div className="mpm-planActions">
                {!(plan.attached_file_path && !plan.ai_generated) && (
                  <button 
                    onClick={() => loadPlanDetails(plan.id)}
                    className="mpm-viewSummaryBtn"
                  >
                    View Summary
                  </button>
                )}
                {(plan.ai_generated || plan.generation_context?.source === 'imported') && (
                  <button
                    onClick={() => {
                      setEditingPlan(plan);
                      openRecipeViewer(plan.id);
                    }}
                    className="mpm-viewRecipesBtn"
                  >
                    View Recipes
                  </button>
                )}
                <button
                  onClick={() => openRenewModal(plan)}
                  className="mpm-renewBtn"
                >
                  Renew
                </button>
                <button
                  onClick={() => handleDelete(plan.id)}
                  className="mpm-deleteBtn"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Form - Manual Entry */}
      {showForm && (
        <div className="mpm-formOverlay">
          <div className="mpm-formModal">
            {/* Modal Header */}
            <div className="mpm-formHeader">
              <h3 className="mpm-formTitle">
                {editingPlan ? 'Edit Meal Plan' : 'Create New Meal Plan'}
              </h3>
              <button
                onClick={closeForm}
                className="mpm-formCloseBtn"
                onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
                onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
              >
                ×
              </button>
            </div>
            
            <div className="mpm-formBody">
              {/* Show AI-specific fields as read-only if viewing AI-generated plan */}
              {editingPlan && editingPlan.ai_generated && (
                <div className="mpm-aiInfoBanner">
                  <strong>AI Generated Meal Plan</strong>
                  {editingPlan.human_edited && (
                    <div className="mpm-editedWarning">
                      <strong>This plan has been manually edited by you</strong>
                    </div>
                  )}
                  {editingPlan.ai_prompt && (
                    <div className="mpm-aiPromptText">
                      <strong>Prompt:</strong> {editingPlan.ai_prompt}
                    </div>
                  )}
                </div>
              )}

              {/* Show attached file if editing plan with file */}
              {editingPlan && editingPlan.attached_file && (
                <div className="mpm-attachedFileBar">
                  <span>
                    <strong>Attached File:</strong> {editingPlan.attached_file_name}
                  </span>
                  <button
                    onClick={() => setShowFileViewer(true)}
                    className="mpm-viewFileBtn"
                  >
                    View File
                  </button>
                </div>
              )}

              {/* File Upload Section - Only for new plans */}
              {!editingPlan && (
                <div className="mpm-uploadSection">
                  <label className="mpm-uploadLabel">
                    Upload Meal Plan File (Optional)
                  </label>
                  <div
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    className="mpm-dropzone" style={{ border: dragActive ? '2px dashed #1E3A5F' : '2px dashed #D1D5DB', backgroundColor: dragActive ? '#EBF0F7' : '#F9FAFB' }}
                  >
                    {uploadedFile ? (
                      <div>
                        <p className="mpm-uploadedFileName">
                          {uploadedFile.name}
                        </p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setUploadedFile(null);
                          }}
                          className="mpm-removeFileBtn"
                        >
                          Remove File
                        </button>
                      </div>
                    ) : (
                      <div>
                        <p className="mpm-dropzoneText">
                          Drag and drop your meal plan file here
                        </p>
                        <p className="mpm-dropzoneOr">
                          or
                        </p>
                        <label
                          className="mpm-browseFilesBtn"
                        >
                          Browse Files
                          <input
                            type="file"
                            accept=".pdf, .doc, .docx, .txt, .jpg, .jpeg, .png"
                            onChange={handleFileInput}
                            className="mpm-fileInputHidden"
                          />
                        </label>
                        <p className="mpm-fileTypesHint">
                          Accepts: PDF, Word, TXT, JPG/PNG
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Plan Name */}
              <div className="mpm-fieldGroup">
                <label className="mpm-fieldLabel">
                  Plan Name *
                </label>
                <input
                  type="text"
                  value={formData.plan_name}
                  onChange={(e) => setFormData({ ...formData, plan_name: e.target.value })}
                  placeholder="My Meal Plan"
                  className="mpm-textInput"
                />
              </div>

              {/* Plan Type */}
              <div className="mpm-typeGroup">
                <label className="mpm-typeLabel">
                  Plan Type
                </label>
                <select
                  value={formData.plan_type}
                  onChange={(e) => setFormData({ ...formData, plan_type: e.target.value })}
                  className="mpm-typeSelect"
                >
                  <option value="maintenance">Maintenance</option>
                  <option value="weight_loss">Weight Loss</option>
                  <option value="muscle_gain">Muscle Gain</option>
                  <option value="athletic">Athletic Performance</option>
                </select>
              </div>

              {/* Calories & Macros */}
              <div className="mpm-caloriesGroup">
                <label className="mpm-caloriesLabel">
                  Daily Calorie Target
                </label>
                <input
                  type="number"
                  value={formData.target_calories_daily}
                  onChange={(e) => setFormData({ ...formData, target_calories_daily: parseInt(e.target.value) })}
                  step="50"
                  className="mpm-caloriesInput"
                />
              </div>

              {/* Macro Percentages */}
              <div className="mpm-macrosSection">
                <label className="mpm-macrosLabel">
                  Macronutrient Distribution (must total 100%)
                </label>
                
                <div className="mpm-proteinSlider">
                  <div className="mpm-proteinRow">
                    <span className="mpm-proteinPercent">Protein: {formData.target_protein_percentage}%</span>
                    <span className="mpm-proteinGrams">{macros.protein}g</span>
                  </div>
                  <input
                    type="range"
                    min="10"
                    max="50"
                    value={formData.target_protein_percentage}
                    onChange={(e) => setFormData({ ...formData, target_protein_percentage: parseInt(e.target.value) })}
                    className="mpm-proteinRange"
                  />
                </div>

                <div className="mpm-carbsSlider">
                  <div className="mpm-carbsRow">
                    <span className="mpm-carbsPercent">Carbs: {formData.target_carbs_percentage}%</span>
                    <span className="mpm-carbsGrams">{macros.carbs}g</span>
                  </div>
                  <input
                    type="range"
                    min="10"
                    max="70"
                    value={formData.target_carbs_percentage}
                    onChange={(e) => setFormData({ ...formData, target_carbs_percentage: parseInt(e.target.value) })}
                    className="mpm-carbsRange"
                  />
                </div>

                <div className="mpm-fatSlider">
                  <div className="mpm-fatRow">
                    <span className="mpm-fatPercent">Fat: {formData.target_fat_percentage}%</span>
                    <span className="mpm-fatGrams">{macros.fat}g</span>
                  </div>
                  <input
                    type="range"
                    min="10"
                    max="50"
                    value={formData.target_fat_percentage}
                    onChange={(e) => setFormData({ ...formData, target_fat_percentage: parseInt(e.target.value) })}
                    className="mpm-fatRange"
                  />
                </div>

                <div className="mpm-macroTotal" style={{ backgroundColor: macroTotal === 100 ? '#D1FAE5' : '#FEE2E2', color: macroTotal === 100 ? '#065F46' : '#991B1B' }}>
                  Total: {macroTotal}% {macroTotal !== 100 && '(Must equal 100%)'}
                </div>
              </div>

              {/* Advanced Options Toggle */}
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="mpm-advancedToggle"
              >
                {showAdvanced ? '▼' : '▶'} Advanced Options
              </button>

              {showAdvanced && (
                <div className="mpm-advancedSection">
                  {/* Meals Per Day */}
                  <div className="mpm-mealsPerDayGroup">
                    <label className="mpm-mealsPerDayLabel">
                      Meals Per Day
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="6"
                      value={formData.meals_per_day}
                      onChange={(e) => setFormData({ ...formData, meals_per_day: parseInt(e.target.value) })}
                      className="mpm-mealsPerDayInput"
                    />
                  </div>

                  {/* Snacks Per Day */}
                  <div className="mpm-snacksGroup">
                    <label className="mpm-snacksLabel">
                      Snacks Per Day
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="4"
                      value={formData.snacks_per_day}
                      onChange={(e) => setFormData({ ...formData, snacks_per_day: parseInt(e.target.value) })}
                      className="mpm-snacksInput"
                    />
                  </div>

                  {/* Water Target */}
                  <div className="mpm-waterGroup">
                    <label className="mpm-waterLabel">
                      Daily Water Target (ml)
                    </label>
                    <input
                      type="number"
                      value={formData.target_water_ml}
                      onChange={(e) => setFormData({ ...formData, target_water_ml: parseInt(e.target.value) })}
                      step="100"
                      className="mpm-waterInput"
                    />
                  </div>
                </div>
              )}

              {/* Active Status */}
              <div className="mpm-activeGroup">
                <label className="mpm-activeLabel">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="mpm-activeCheckbox"
                  />
                  <span className="mpm-activeText">Make this my active meal plan</span>
                </label>
              </div>

              {/* Action Buttons */}
              <div className="mpm-formActions">
                <button 
                  onClick={closeForm}
                  className="mpm-cancelBtn"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleSubmit}
                  disabled={macroTotal !== 100}
                  className="mpm-submitBtn" style={{ backgroundColor: macroTotal === 100 ? '#1E3A5F' : '#D1D5DB', cursor: macroTotal === 100 ? 'pointer' : 'not-allowed', opacity: macroTotal === 100 ? 1 : 0.6 }}
                >
                  {editingPlan ? 'Update Plan' : 'Create Plan'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detailed Meal Plan Viewer Modal */}
      {showDetailedView && selectedPlanDetails && (
        <div className="mpm-detailOverlay">
          <div className="mpm-detailModal">
            {/* Header */}
            <div className="mpm-detailHeader">
              <h3 className="mpm-detailTitle">
                {selectedPlanDetails.plan_name}
              </h3>
              <button
                onClick={() => {
                  setShowDetailedView(false);
                  setSelectedPlanDetails(null);
                }}
                className="mpm-detailCloseBtn"
              >
                ×
              </button>
            </div>

            {/* Plan Details - Scrollable */}
            <div className="mpm-detailBody">
              {/* Summary Info */}
              <div className="mpm-summaryCard">
                <div className="mpm-summaryGrid">
                  <div>
                    <div className="mpm-statLabel">Daily Calories</div>
                    <div className="mpm-statValue">
                      {selectedPlanDetails.target_calories_daily} cal
                    </div>
                  </div>
                  <div>
                    <div className="mpm-statLabelProtein">Protein</div>
                    <div className="mpm-statValueProtein">
                      {selectedPlanDetails.target_protein_percentage}%
                    </div>
                  </div>
                  <div>
                    <div className="mpm-statLabelCarbs">Carbs</div>
                    <div className="mpm-statValueCarbs">
                      {selectedPlanDetails.target_carbs_percentage}%
                    </div>
                  </div>
                  <div>
                    <div className="mpm-statLabelFat">Fat</div>
                    <div className="mpm-statValueFat">
                      {selectedPlanDetails.target_fat_percentage}%
                    </div>
                  </div>
                </div>
              </div>

              {/* Daily Schedule */}
              {selectedPlanDetails.schedule && selectedPlanDetails.schedule.length > 0 ? (
                selectedPlanDetails.schedule.map((day, dayIndex) => (
                  <div key={dayIndex} className="mpm-dayCard">
                    <div className="mpm-dayHeader">
                      <span>Day {day.day_number}</span>
                    </div>
                    
                    {day.meals && day.meals.map((meal, mealIndex) => (
                      <div key={mealIndex} className="mpm-mealItem" style={{ borderBottom: mealIndex < day.meals.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                        <div className="mpm-mealType">
                          {meal.meal_type} - {meal.target_time || 'Flexible timing'}
                        </div>
                        
                        <div className="mpm-mealName">
                          {meal.recipe_name || meal.meal_name}
                        </div>

                        {meal.description && (
                          <div className="mpm-mealDescription">
                            {meal.description}
                          </div>
                        )}

                        <div className="mpm-mealNutrition">
                          {meal.calories && <span>{meal.calories} cal</span>}
                          {meal.protein_grams && <span>{meal.protein_grams}g protein</span>}
                          {meal.carbs_grams && <span>{meal.carbs_grams}g carbs</span>}
                          {meal.fat_grams && <span>{meal.fat_grams}g fat</span>}
                        </div>
                        {/* View Recipe Button for Individual Meal.
                            Under two-phase generation the card may not exist yet — this builds
                            it on demand (~7s, once) and then opens the same viewer as before. */}
                        {(selectedPlanDetails.ai_generated || selectedPlanDetails.generation_context?.source === 'imported') && (
                          <button
                            onClick={() => {
                              // Set selected day recipes with ONLY this specific meal
                              setSelectedDayRecipes({
                                day_number: day.day_number,
                                day_name: day.day_name,
                                meals: [meal]  // ONLY this single meal
                              });
                              ensureCardThenViewRecipe(
                                selectedPlanDetails?.id || editingPlan?.id, meal, day.day_number);
                            }}
                            disabled={cardBuilding === meal.schedule_id}
                            className="mpm-viewRecipeBtn"
                          >
                            {cardBuilding === meal.schedule_id
                              ? 'Building your recipe…'
                              : 'View Recipe'}
                          </button>
                        )}
                        {cardBuilding === meal.schedule_id && (
                          <div className="mpm-cardBuilding">
                            Writing the quantities, method and a photo for this meal — about 7
                            seconds. It is saved after this, so opening it again is instant.
                          </div>
                        )}
                        {cardError && cardErrorFor === meal.schedule_id && (
                          <div className="mpm-cardError">{cardError}</div>
                        )}
                      </div>
                    ))}
                  </div>
                ))
              ) : (
                <div className="mpm-noSchedule">
                  <p className="mpm-noScheduleIcon"></p>
                  <p className="mpm-noScheduleText">No detailed schedule available for this plan.</p>
                  <p className="mpm-noScheduleHint">
                    The meal plan structure has been created but daily meals haven't been scheduled yet.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* File Viewer Modal */}
      {showFileViewer && editingPlan?.attached_file_path && (
        <div className="mpm-fileViewerOverlay">
          <div className="mpm-fileViewerModal">
            {/* Header */}
            <div className="mpm-fileViewerHeader">
              <div>
                <h3 className="mpm-fileViewerTitle">
                  {editingPlan.attached_file_name}
                </h3>
                <p className="mpm-fileViewerSize">
                  {(editingPlan.attached_file_size / 1024).toFixed(1)} KB
                </p>
              </div>
              <button
                onClick={() => { closeFileViewer(); }}
                className="mpm-fileViewerCloseBtn"
                onMouseOver={(e) => e.target.style.backgroundColor = '#E5E7EB'}
                onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                ×
              </button>
            </div>

            {/* File Content */}
            <div className="mpm-fileViewerBody" style={fileContent.startsWith('__PDF__') ? { padding: 0 } : {}}>
              {fileContent.startsWith('__PDF__') ? (
                <iframe
                  src={fileContent.replace('__PDF__', '')}
                  style={{ width: '100%', minHeight: '70vh', border: 'none', display: 'block' }}
                  title="PDF Viewer"
                />
              ) : (
                <pre className="mpm-fileViewerPre">
                  {fileContent}
                </pre>
              )}
            </div>

            {/* Footer with Dismiss Button */}
            <div className="mpm-fileViewerFooter">
              {fileContent.startsWith('__PDF__') ? (
                <span className="mpm-fileViewerHint">Use the PDF toolbar to Print</span>
              ) : (
                <button
                  onClick={() => printFileContent(fileContent, editingPlan?.attached_file_name)}
                  className="mpm-fileViewerPrintBtn"
                >
                  Print
                </button>
              )}
              <button
                onClick={() => { closeFileViewer(); }}
                className="mpm-fileViewerDismissBtn"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Day Recipe Viewer Modal */}
      {showDayRecipeViewer && selectedDayRecipes && selectedPlanDetails && (
        <div className="mpm-dayRecipeOverlay">
          <div className="mpm-dayRecipeModal">
            {/* Header */}
            <div className="mpm-dayRecipeHeader">
              <h3 className="mpm-dayRecipeTitle">
                {selectedDayRecipes.day_name || `Day ${selectedDayRecipes.day_number}`} Recipe
              </h3>
              <button
                onClick={() => closeDayRecipeViewer()}
                className="mpm-dayRecipeCloseBtn"
              >
                ×
              </button>
            </div>

            {/* Recipe Content - Scrollable */}
            <div className="mpm-dayRecipeBody">
              {/* #93 — THE VERDICT THE BACKEND ALWAYS COMPUTED AND NOBODY EVER SHOWED.
                  `gate_card` re-screens the finished card against the COMPLETE UHC using the
                  nutrients that quantities make measurable — seven rules (sodium, potassium,
                  phosphorus, added sugar, saturated fat …) that CANNOT fire on ingredient names
                  alone. `avoid` is refused outright and never reaches here; `caution` and
                  `advisory` were computed, attached, and silently discarded, so a user could be
                  served a meal that interacts with their medication and see nothing.
                  UhcCompatBadge renders null when the verdict is clear, so this costs nothing
                  on a safe recipe. */}
              {cardReplaced && (
                <div className="mpm-cardReplaced" role="note">
                  We swapped <strong>{cardReplaced}</strong> for this recipe. Once the exact
                  quantities were worked out, the original went over a limit in your health
                  profile — so it was replaced with something that fits. Only this meal changed.
                </div>
              )}
              <UhcCompatBadge compatibility={cardSafety} />
              {dayRecipeUrl ? (
                <iframe
                  src={dayRecipeUrl}
                  className="mpm-dayRecipeIframe"
                  title={`Day ${selectedDayRecipes.day_number} Recipe`}
                />
              ) : (
                <div className="mpm-dayRecipeIframe" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280' }}>
                  Loading recipe…
                </div>
              )}
            </div>

            {/* Footer with Make Recipe Button */}
            <div className="mpm-dayRecipeFooter">
              <button
                onClick={() => closeDayRecipeViewer()}
                className="mpm-dayRecipeCloseFooterBtn"
              >
                Close
              </button>
              <button
                onClick={async () => {
                  try {
                    const response = await fetchWithAuth(
                      `${API_BASE}/api/meal-plans/${selectedPlanDetails.id}/make-recipe`,
                      {
                        method: 'POST',
                        body: JSON.stringify({ day_number: selectedDayRecipes.day_number })
                      }
                    );

                    if (response.ok) {
                      const result = await response.json();
                      const lines = [`${result.recipe_name || `Day ${selectedDayRecipes.day_number}`} — Pantry Updated\n`];
                      if (result.items_decremented?.length > 0) {
                        lines.push('Used from pantry:');
                        result.items_decremented.forEach(i => lines.push(`  ${i.name}: ${i.previous_qty} → ${i.new_qty}`));
                      }
                      if (result.items_depleted?.length > 0) {
                        lines.push('\nUsed up completely:');
                        result.items_depleted.forEach(i => lines.push(`  ${i.name} (was ${i.previous_qty})`));
                      }
                      if (result.items_missing?.length > 0) {
                        lines.push('\nNot in pantry:');
                        result.items_missing.forEach(i => lines.push(`  ${i.name} (${i.quantity} ${i.unit})`));
                      }
                      if (result.items_opened?.length > 0) {
                        lines.push('\nOpened (new expiry):');
                        result.items_opened.forEach(i => lines.push(`  ${i.name} — use by ${i.new_expiry}`));
                      }
                      if (result.items_thawed?.length > 0) {
                        lines.push('\nAuto-thawed:');
                        result.items_thawed.forEach(i => lines.push(`  ${i.name} — use within ${i.days_to_use} days`));
                      }
                      alert(lines.join('\n'));
                    } else {
                      const errData = await response.json().catch(() => ({}));
                      const detail = errData.detail || 'Failed to update pantry inventory';
                      alert(typeof detail === 'string' ? detail : detail.map(d => d.msg || d).join(', '));
                    }
                  } catch (error) {
                    console.error('Error making recipe:', error);
                    alert('Error updating pantry inventory: ' + error.message);
                  }
                }}
                className="mpm-makeRecipeBtn"
              >
                Make Recipe
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Plan Modal */}
      {showImportModal && (
        <div className="mpi-overlay" onClick={closeImportModal}>
          <div className="mpi-modal" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="mpi-header">
              <h3 className="mpi-title">{importPreview ? 'Review Imported Plan' : 'Import Meal Plan'}</h3>
              <button className="mpi-close" onClick={closeImportModal}>×</button>
            </div>

            <div className="mpi-body">
              {!importPreview ? (
                <>
                  {/* Input Mode Tabs */}
                  <div className="mpi-tabs">
                    <button
                      className={`mpi-tab ${importMode === 'file' ? 'mpi-tab--active' : ''}`}
                      onClick={() => setImportMode('file')}
                    >Upload File</button>
                    <button
                      className={`mpi-tab ${importMode === 'text' ? 'mpi-tab--active' : ''}`}
                      onClick={() => setImportMode('text')}
                    >Paste Text</button>
                    <button
                      className={`mpi-tab ${importMode === 'url' ? 'mpi-tab--active' : ''}`}
                      onClick={() => setImportMode('url')}
                    >Import URL</button>
                  </div>

                  {/* Tab 1: Upload File */}
                  {importMode === 'file' && (
                    <div
                      className="mpi-dropzone"
                      onDragEnter={handleImportDrag}
                      onDragLeave={handleImportDrag}
                      onDragOver={handleImportDrag}
                      onDrop={handleImportDrop}
                    >
                      {importFile ? (
                        <div className="mpi-fileSelected">
                          <p className="mpi-fileName">{importFile.name}</p>
                          <button onClick={() => setImportFile(null)} className="mpi-removeFileBtn">Remove</button>
                        </div>
                      ) : (
                        <div className="mpi-dropzoneContent">
                          <p className="mpi-dropzoneText">Drag and drop your meal plan file here</p>
                          <p className="mpi-dropzoneOr">or</p>
                          <label className="mpi-browseBtn">
                            Browse Files
                            <input
                              type="file"
                              accept=".pdf,.txt,.jpg,.jpeg,.png"
                              onChange={handleImportFileInput}
                              className="mpi-fileInputHidden"
                            />
                          </label>
                          <p className="mpi-fileHint">Accepts: PDF, TXT, JPG/PNG</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Tab 2: Paste Text */}
                  {importMode === 'text' && (
                    <textarea
                      className="mpi-textarea"
                      value={importText}
                      onChange={(e) => setImportText(e.target.value)}
                      placeholder="Paste your meal plan text here...&#10;&#10;Example:&#10;Day 1:&#10;Breakfast - Oatmeal with berries&#10;Lunch - Grilled chicken salad&#10;Dinner - Salmon with roasted vegetables"
                      rows={10}
                    />
                  )}

                  {/* Tab 3: Import URL */}
                  {importMode === 'url' && (
                    <input
                      type="url"
                      className="mpi-urlInput"
                      value={importUrl}
                      onChange={(e) => setImportUrl(e.target.value)}
                      placeholder="https://www.allrecipes.com/recipe/..."
                    />
                  )}

                  {/* Parse Button */}
                  <div className="mpi-actions">
                    {/* B-CREDIT1 fix 2: AI parsing costs `meal_plan_import` credits. A link the recipe
                        reader can parse on its own is free, so URL mode states that instead of a price. */}
                    <button
                      onClick={() => spend('meal_plan_import', 'Import this meal plan?', handleImportParse)}
                      className="mpi-parseBtn"
                      disabled={importLoading}
                    >
                      {importLoading
                        ? <><span className="mpi-spinner"></span>Analyzing...</>
                        : <>Parse Meal Plan {importMode !== 'url' && <CostChip apiType="meal_plan_import" />}</>}
                    </button>
                    {importMode === 'url' && !importLoading && (
                      <p className="mpi-costNote">
                        Free when the page is a recipe we can read directly; otherwise <CostChip apiType="meal_plan_import" />
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* Editable Preview */}
                  <div className="mpi-planMeta">
                    <div className="mpi-metaRow">
                      <label className="mpi-metaLabel">Plan Name</label>
                      <input
                        type="text"
                        className="mpi-metaInput"
                        value={importPlanName}
                        onChange={(e) => setImportPlanName(e.target.value)}
                      />
                    </div>
                    <div className="mpi-metaRow">
                      <label className="mpi-metaLabel">Plan Type</label>
                      <select
                        className="mpi-metaSelect"
                        value={importPlanType}
                        onChange={(e) => setImportPlanType(e.target.value)}
                      >
                        <option value="maintenance">Maintenance</option>
                        <option value="weight_loss">Weight Loss</option>
                        <option value="weight_gain">Weight Gain</option>
                        <option value="performance">Performance</option>
                        <option value="medical">Medical</option>
                      </select>
                    </div>
                  </div>

                  <div className="mpi-preview">
                    {importPreview.days.map((day, dayIdx) => (
                      <div key={dayIdx} className="mpi-dayCard">
                        <h4 className="mpi-dayTitle">{day.day_name || `Day ${day.day_number}`}</h4>
                        {day.meals.map((meal, mealIdx) => (
                          <div key={mealIdx} className="mpi-mealRow">
                            <select
                              value={meal.meal_type}
                              onChange={(e) => updateImportMeal(dayIdx, mealIdx, 'meal_type', e.target.value)}
                              className="mpi-mealType"
                            >
                              <option value="breakfast">Breakfast</option>
                              <option value="lunch">Lunch</option>
                              <option value="dinner">Dinner</option>
                              <option value="snack">Snack</option>
                            </select>
                            <input
                              value={meal.meal_name}
                              onChange={(e) => updateImportMeal(dayIdx, mealIdx, 'meal_name', e.target.value)}
                              className="mpi-mealName"
                            />
                            <span className="mpi-mealCals">{meal.calories || '—'} cal</span>
                            <button
                              onClick={() => removeImportMeal(dayIdx, mealIdx)}
                              className="mpi-removeMealBtn"
                              title="Remove meal"
                            >×</button>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>

                  {/* Confirm / Back */}
                  <div className="mpi-actions">
                    <button
                      onClick={() => setImportPreview(null)}
                      className="mpi-backBtn"
                    >Back</button>
                    <button
                      onClick={handleImportConfirm}
                      className="mpi-confirmBtn"
                      disabled={importLoading || !importPreview.days.length}
                    >
                      {importLoading ? <><span className="mpi-spinner"></span>Creating...</> : `Create Plan (${importPreview.days.reduce((sum, d) => sum + d.meals.length, 0)} meals)`}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Recipe Viewer Modal */}
      {showRecipeViewer && editingPlan && (
        <div className="mpm-recipeViewerOverlay">
          <div className="mpm-recipeViewerModal">
            {/* Header */}
            <div className="mpm-recipeViewerHeader">
              <div>
                <h3 className="mpm-recipeViewerTitle">
                  {editingPlan.plan_name} - Recipes
                </h3>
                <p className="mpm-recipeViewerSubtitle">
                  All recipes for this meal plan
                </p>
              </div>
              <button
                onClick={() => closeRecipeViewer()}
                className="mpm-recipeViewerCloseBtn"
                onMouseOver={(e) => e.target.style.backgroundColor = '#E5E7EB'}
                onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                ×
              </button>
            </div>

            {/* Recipe Content */}
            <div className="mpm-recipeViewerBody">
              {recipeViewerUrl ? (
                <iframe
                  src={recipeViewerUrl}
                  className="mpm-recipeViewerIframe"
                  title="Recipe Viewer"
                />
              ) : (
                <div className="mpm-recipeViewerIframe" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280' }}>
                  Loading recipes…
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="mpm-recipeViewerFooter">
              <button
                onClick={async () => {
                  try {
                    // H7: Authorization header, not ?token= in the URL.
                    const resp = await fetchWithAuth(`${API_BASE}/api/meal-plans/${editingPlan.id}/recipes/view`);
                    const html = await resp.text();
                    printHTMLDocument(html, `${editingPlan.plan_name} - Recipes`);
                  } catch (err) {
                    console.error('Print failed:', err);
                  }
                }}
                className="mpm-recipeViewerPrintBtn"
              >
                Print
              </button>
              <button
                onClick={() => closeRecipeViewer()}
                className="mpm-recipeViewerDismissBtn"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Renew Modal (Phase D) */}
      {showRenewModal && renewingPlan && (
        <div className="modal-overlay" onClick={() => setShowRenewModal(false)}>
          <div className="modal-content modal-content-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Renew Meal Plan</h3>
              <button className="modal-close" onClick={() => setShowRenewModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="mpm-renewPlanInfo">
                <strong>{renewingPlan.plan_name}</strong>
                <span className="mpm-renewPlanMeta">
                  {renewingPlan.plan_type || 'Maintenance'} &bull; {renewingPlan.target_calories_daily} cal/day
                </span>
              </div>

              <div className="mpm-renewField">
                <label className="mpm-renewLabel">New Start Date</label>
                <input
                  type="date"
                  className="mpm-renewInput"
                  value={renewStartDate}
                  onChange={e => setRenewStartDate(e.target.value)}
                />
                <span className="mpm-renewHint">
                  Meals will be scheduled from this date using the plan's existing recipes.
                </span>
              </div>

              <div className="mpm-renewRecurringSection">
                <label className="mpm-renewLabel">Recurring Plan</label>
                <div className="mpm-renewRecurringRow">
                  <div
                    className={`mpm-toggleSwitch ${renewRecurring ? 'mpm-toggleSwitch--active' : ''}`}
                    onClick={() => setRenewRecurring(!renewRecurring)}
                  >
                    <div className="mpm-toggleDot" />
                  </div>
                  <span>Auto-extend every</span>
                  <input
                    type="number"
                    className="mpm-renewDaysInput"
                    value={renewDurationDays}
                    onChange={e => setRenewDurationDays(parseInt(e.target.value) || 7)}
                    min={1}
                    max={90}
                    disabled={!renewRecurring}
                  />
                  <span>days</span>
                </div>
                <span className="mpm-renewHint">
                  Plan automatically renews when the current period ends.
                </span>
              </div>

              <div className="mpm-renewInfoBox">
                <strong>What happens on Renew:</strong>
                <ol>
                  <li>Existing schedule items shifted to new date range (same recipes, NO AI call)</li>
                  <li>This plan becomes the active plan (others deactivated)</li>
                </ol>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowRenewModal(false)}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleRenew}
                disabled={renewLoading || !renewStartDate}
              >
                {renewLoading ? 'Renewing...' : 'Renew & Activate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MealPlanManagement;